import { CollectionTarget } from "@ciaopeople/cp-shatsu/DomHelper";
import { EventHandler, SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { isBoolean } from "@ciaopeople/cp-shatsu/TypeChecking";
import { flatten, hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { GroupableData } from "../../api/commondata/GroupableData.js";
import { UserActionError, UserActionErrorCode, UserActionIdentifier } from "../../api/commondata/UserActionError.js";
import { RESTAPIEventNames, RESTAPIEvents } from "../../api/RESTAPIEvents.js";
import { RESTUserServices } from "../../api/RESTUserServices.js";
import { ContentRestrictionCriteria, isTopicQueryByExtendedId, isTopicQueryByShortId, isTopicQueryByUrl, isTopicQueryByWordpressId, isTopicQueryObjWithSubscribe, TopicQueryObj, TopicQueryObjWithSubscribe, UserActionExtraInfo } from "../../api/types/NotificationDataTypes.js";
import { AAABarrierWithSource } from "../aaa/AAABarrierWithSource.js";
import { ActionableEvents, AttachAndStatusData, ElementWithAttachData, ParseAndAttachModule } from "./ParseAndAttachModule.js";
import { SdkHandledHTMLElement, SubscriptionCallbackData } from "./SdkHandledHTMLElement.js";

type Action = (evt: string, data?: any) => void;



export class NewsletterSubscriptionHTMLElement extends SdkHandledHTMLElement<boolean> {
    
    
    public setStatus(s: boolean): SdkHandledHTMLElement<boolean> {
        this.attr("data-subscribed", s?"true":"false");
        return this;
    }
    public getStatus(): boolean {
        return this.firstAttr("data-subscribed") == "true";
    }

    public getUrl(): string {
        return this.firstAttr("data-url");
    }
 
    public getUTMSource(): string {
        const incomingSource = super.getUTMSource();
        return incomingSource && incomingSource != ""?incomingSource:"newsletter-subscribe";
    }
    
    public getAssociatedUserAction(): UserActionIdentifier {
        return UserActionIdentifier.NEWSLETTER_SUBSCRIBE;
    }


    

}


class GroupableTopicQueryObj implements GroupableData {


    constructor(public readonly query: TopicQueryObj) {

    }

    /**
     * 
     * @returns null se non matcha nessun tipo conosciuto di topic query
     */
    getGroupName(): string {
        if(isTopicQueryByUrl(this.query)) {
            return `[url]-${this.query.url}`;
        }
        else if(isTopicQueryByExtendedId(this.query)) {
            return `[extid]-${this.query.extId}`;
        }
        else if(isTopicQueryByShortId(this.query)) {
            return `[sid]-${this.query.hint}-${this.query.id}`;
        }
        else if(isTopicQueryByWordpressId(this.query)) {
            return `[wpid]-${this.query.hint}-${this.query.wpid}`;
        }
        else return null;
    }


}


export interface NewsletterSubscriptionEventWithInvolvedDOM extends TopicQueryObjWithSubscribe {
    dom: HTMLElement[]
}

export function isNewsletterSubscriptionEventWithInvolvedDOM(obj: any): obj is NewsletterSubscriptionEventWithInvolvedDOM {
    return isTopicQueryObjWithSubscribe(obj) && hasOwnProperty(obj, "dom");
}




export class NewsletterParseAndAttachModule extends ParseAndAttachModule<boolean, GroupableTopicQueryObj> implements EventHandler {


    private eh: EventHandler;


    constructor(private restServices: AAABarrierWithSource<SdkHandledHTMLElement<any>, RESTUserServices>, 
                private restEvents: RESTAPIEvents) {
        super();
        this.eh = new SimpleEventHandler();
        this.restEvents.onNewsletterSubscriptionStatusChange((subscriptionData) => {
            if(isTopicQueryByUrl(subscriptionData.query)) {
                this.updatedElementStatuses(
                    [{
                        attachData: new GroupableTopicQueryObj(subscriptionData.query),
                        status: subscriptionData.subscribed,
                        restriction: subscriptionData.restriction
                    }]                
                );
            }   
        });
    }

    

    protected extractAndCreate(el: HTMLElement): ElementWithAttachData<boolean, GroupableTopicQueryObj> {
        const parsed = new NewsletterSubscriptionHTMLElement(el);
        if(parsed.getUrl() != "") {
            return {
                element: parsed,
                attachData: new GroupableTopicQueryObj({
                    url: parsed.getUrl()
                })
            };
        }
        else return null;
    }

    public action(el: ElementWithAttachData<boolean, GroupableTopicQueryObj>): void {

        const subscribeElement = el.element;
        
                
        
        this.restServices.executeWithBarrierWithSource(subscribeElement, service => {
            const subscribeElemStatus = subscribeElement.getStatus();
            const umed = subscribeElement.getUTMmedium();
            const maybeExtraInfo: UserActionExtraInfo = umed && umed != ""?{ source_element: umed }:null;

            const [actionPromise,isNoOp] = subscribeElemStatus?
                (el.element.isSubscriptionOnly()?[Promise.resolve(true),true]:[service.unsubscribeNewsletter(el.attachData.query, maybeExtraInfo),false]):
                [service.subscribeNewsletter(el.attachData.query, maybeExtraInfo),false];
            
            return actionPromise.then(res =>  {
                const newSubscriptionStatus = isBoolean(res)?res:(res.data.subscribed?true:false);
                subscribeElement.setStatus(newSubscriptionStatus);
                if(!isNoOp) {
                    this.success.next( {
                        ...el,
                        action: UserActionIdentifier.NEWSLETTER_SUBSCRIBE
                    });
                }
                return newSubscriptionStatus;
            }).then(res => {
                
                if(res) {
                    return service.getMe(true).then(uresp => {
                        const data: SubscriptionCallbackData = {
                            subscription_intent: res,
                            is_subscription_changed: subscribeElemStatus != res,
                            user: {
                                id: uresp.data.id,
                                first_name: uresp.data.nameAndGender?.first_name,
                                last_name: uresp.data.nameAndGender?.last_name,
                                email: uresp.data.email
                            },
                            redirect_url: el.element.getRedirectUrl(),
                            topic_label: subscribeElement.getTopicLabel(),
                            user_was_already_registered: true                        
                        };
                        try {   
                            subscribeElement.executeAfterSubscriptionCallback(data);
                        }
                        catch(ex) {
                            console.error("There was an error executing external callback");
                            console.error(ex);
                        }
                        return res;
                    });                    
                }
                else return Promise.resolve(res);
            }).catch(err => {
                const uaErr: UserActionError = {
                    code: UserActionErrorCode.UNKNOWN_ERROR,
                    action: UserActionIdentifier.NEWSLETTER_SUBSCRIBE,
                    message: err,
                    data: {
                        query: el.attachData
                    }
                };
                if(!isNoOp) {
                    this.errors.next(uaErr);
                }
                return Promise.reject(uaErr);
            });
        });
        
    }


    public parseAndAttach(el: CollectionTarget | CollectionTarget[], _trigger?: never): Promise<void> {
        return super.parseAndAttach(el, ActionableEvents.click);
    }

    protected afterAttach(newlyAttached: ElementWithAttachData<boolean, GroupableTopicQueryObj>[]): Promise<void> {
        if(newlyAttached && newlyAttached.length > 0) {
            const checkReq = {
                queries: newlyAttached.map(el => el.attachData.query)
            };
            return this.restServices.getBarricadedService().checkNewsletterSubscriptions(checkReq).then();
        }
        else return Promise.resolve();
    }




    protected afterUpdate(s: AttachAndStatusData<boolean, GroupableTopicQueryObj>, involvedElements: SdkHandledHTMLElement<boolean>[]): void {
        this.triggerNewsletterSubscriptionStatusChange(         
            {
                dom: flatten(involvedElements.map(ie => ie.ig())),
                query: s.attachData.query,
                subscribed: s.status,
                restriction: involvedElements && involvedElements.length > 0?involvedElements[0].getContentRestrictionCriteria():ContentRestrictionCriteria.ACCESSIBLE_TO_ALL
            }
        );
    }


    private triggerNewsletterSubscriptionStatusChange(evt: NewsletterSubscriptionEventWithInvolvedDOM): void {
        this.trigger(RESTAPIEventNames.NEWSLETTER_SUBSCRIPTION_STATUS_CHANGE, evt);
    }


    public onNewsletterSubscriptionStatusChange(fun: (evt: NewsletterSubscriptionEventWithInvolvedDOM) => any): void {
        this.on(RESTAPIEventNames.NEWSLETTER_SUBSCRIPTION_STATUS_CHANGE, (evt, data) => {
            if(isNewsletterSubscriptionEventWithInvolvedDOM(data)) fun(data);
        });
    }

    on(eventName: string, callback: Action) {
        this.eh.on(eventName, callback);
    }
    off(act: Action);
    off(evt: string, act: Action);
    off(evtOrAct: string | Action, act?: Action);
    off(evtOrAct: string | Action, act?: Action): any {
        return this.eh.off(evtOrAct, act);    
    }
    trigger(eventName: string, data: any): void {
        this.eh.trigger(eventName, data);
    }
    one(eventName: string, callback: Action): void {
        this.eh.one(eventName, callback);
    }

}